/**
 * Education queue strategy - handles education discovery with custom user input support
 */

import { generateEducationData } from './utils';
import { StrategyPhase } from '../baseStrategy';
import { fetchChatCompletion, updateQueueItem } from '../../api';
import centralDiscoveryQueue from '../../centralDiscoveryQueue';
import { API_CONFIG } from '../../../config/constants';

/**
 * Check if we need to generate the next education item in the sequence
 * This implements progressive discovery - only show the next item after the current one is completed
 * @param {Object} decision - Current decision object
 */
const checkAndGenerateNextEducationItem = async (decision) => {
  try {
    console.log('🎓 EDUCATION STRATEGY: Checking for next education item to generate');
    
    const existingEducationItems = centralDiscoveryQueue.getItemsByTopic('education');
    console.log('🎓 EDUCATION STRATEGY: Found', existingEducationItems.length, 'existing education items');
  
  // Check completion status of existing items
  const completedItems = existingEducationItems.filter(item => item.disco_done === true);
  const pendingItems = existingEducationItems.filter(item => item.disco_done !== true);
  
  console.log('🎓 EDUCATION STRATEGY: Education items status:', {
    total: existingEducationItems.length,
    completed: completedItems.length,
    pending: pendingItems.length
  });
  
  // If there are still pending items, don't generate new ones
  if (pendingItems.length > 0) {
    console.log('🎓 EDUCATION STRATEGY: Still have pending education items, not generating new ones');
    return;
  }
  
  // Progressive generation based on what's been completed
  if (completedItems.length === 0) {
    // No items yet, generate degrees
    console.log('🎓 EDUCATION STRATEGY: No items completed, should generate degrees');
    return; // This will be handled by the main logic
  } else if (completedItems.length === 1 && completedItems.some(item => item.section_id === 'edu')) {
    // Degrees completed, generate majors
    console.log('🎓 EDUCATION STRATEGY: Degrees completed, generating majors');
    await generateEducationData(null, null, decision, true, 'majors_only');
  } else if (completedItems.length === 2 && completedItems.some(item => item.section_id === 'major')) {
    // Majors completed, generate schools
    console.log('🎓 EDUCATION STRATEGY: Majors completed, generating schools');
    await generateEducationData(null, null, decision, true, 'schools_only');
  } else if (completedItems.length >= 3 && completedItems.some(item => item.section_id === 'schools')) {
    // Schools completed, generate academic attributes if not already present
    const hasAcademicItem = existingEducationItems.some(item => item.section_id === 'fmmagic');
    if (!hasAcademicItem) {
      console.log('🎓 EDUCATION STRATEGY: Schools completed, generating academic attributes');
      await generateEducationData(null, null, decision, true, 'academic_only');
    }
  }
  
  } catch (error) {
    console.error('🎓 EDUCATION STRATEGY: Error in checkAndGenerateNextEducationItem:', error);
    // Don't throw the error, just log it and continue
  }
};

/**
 * Education strategy function
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Object} queries - Queries object
 * @returns {Object} Updated decision object
 */
const educationStrategy = async (decision, messages, queries = {}) => {
  console.log('🎓 EDUCATION STRATEGY - Processing education topic');
  console.log('🎓 EDUCATION STRATEGY - Input params:', {
    hasDecision: !!decision,
    messagesLength: messages ? messages.length : 0,
    queriesCount: Object.keys(queries).length,
    sessionId: decision?.sessionId,
    currentDiscoveryState: decision?.updatedDiscoveryState
  });
  
  try {
    // First, check if we need to generate the next education item in sequence
    console.log('🎓 EDUCATION STRATEGY - Calling checkAndGenerateNextEducationItem');
    await checkAndGenerateNextEducationItem(decision);
    console.log('🎓 EDUCATION STRATEGY - checkAndGenerateNextEducationItem completed');
    
    // Check if user provided custom input (like custom school names)
    let hasUserMessage = false;
    let userInput = '';
    
    if (messages && messages.length > 0) {
      console.log('🎓 EDUCATION STRATEGY: User provided messages, checking for custom education input');
      hasUserMessage = true;
      userInput = messages[messages.length - 1]?.content || '';
      
      // Check if there are existing university queue items to update
      const allQueueItems = centralDiscoveryQueue.getAllItems();
      const universityQueueItem = allQueueItems.find(item => 
        item.topic === 'education' && item.section_id === 'education_universities'
      );
      
      if (universityQueueItem) {
        console.log('🎓 EDUCATION STRATEGY: Found existing university queue item, parsing user input for schools');
        
        try {
          // Use LLM to parse school names from user input
          const agentPrompt = `You are helping extract university/school names from user input for recruitment purposes.

The user is specifying universities or schools they want to target for candidate recruitment.

User input: "${userInput}"

CRITICAL: You must return ONLY a valid JSON object in this EXACT format:
{
  "schools": ["School Name 1", "School Name 2", "School Name 3"]
}

The response will be parsed using JavaScript JSON.parse() and accessed as parsed.schools, so the JSON must be perfect.

Guidelines for extracting schools:
- Extract only legitimate university/college/school names (not departments or programs)
- Include common variations (e.g., "MIT" and "Massachusetts Institute of Technology") 
- Focus on educational institutions suitable for professional recruitment
- If no clear school names found, return: {"schools": []}
- Limit to maximum 10 schools
- Use the most commonly known official name format
- Handle abbreviations: MIT, UCLA, NYU, etc.

Examples of valid responses:
{"schools": ["Stanford University", "MIT", "Harvard University"]}
{"schools": ["University of California Berkeley", "UCLA"]}
{"schools": []}

IMPORTANT: Return ONLY the JSON object, no explanations, no markdown, no other text whatsoever.`;

          console.log('🎓 EDUCATION STRATEGY: Calling LLM to parse school names...');
          const llmResponse = await fetchChatCompletion(
            API_CONFIG.url,
            userInput,
            agentPrompt
          );
          
          console.log('🎓 EDUCATION STRATEGY: LLM response:', llmResponse);
          
          // Parse the LLM response
          let parsedSchools = [];
          try {
            const parsed = JSON.parse(llmResponse);
            if (parsed.schools && Array.isArray(parsed.schools)) {
              parsedSchools = parsed.schools.filter(school => school && school.trim() !== '');
            }
          } catch (parseError) {
            console.error('🎓 EDUCATION STRATEGY: Error parsing LLM response:', parseError);
            // Try to extract school names using regex as fallback
            const schoolRegex = /(?:university|college|institute|school)\s+(?:of\s+)?([a-zA-Z\s&]+)|([a-zA-Z\s&]+)\s+(?:university|college|institute|school)/gi;
            const matches = userInput.match(schoolRegex);
            if (matches) {
              parsedSchools = matches.slice(0, 10); // Limit to 10
            }
          }
          
          if (parsedSchools.length > 0) {
            console.log('🎓 EDUCATION STRATEGY: Extracted schools:', parsedSchools);
            
            // Update the university queue item with new school suggestions
            const updatedUniversityBoolStr = parsedSchools.map(school => 
              `education.school_name:"${school}"`
            ).join(' OR ');
            
            const updatedUniversityContext = parsedSchools.reduce((acc, school, index) => {
              acc[`university_${index}`] = school;
              return acc;
            }, {});
            
            const updatedQueueItem = {
              ...universityQueueItem,
              bool_str: `(${updatedUniversityBoolStr})`,
              req_cxt: {
                university_cxt: updatedUniversityContext
              },
              ai_intro_message: `Based on your request, here are the universities you mentioned 🎓\nSelect any universities you'd like to target! 🎯`,
              updated_at: Date.now()
            };
            
            console.log('🎓 EDUCATION STRATEGY: Updating university queue item with:', {
              schools: parsedSchools.length,
              boolStr: updatedUniversityBoolStr.substring(0, 100) + '...'
            });
            
            // Update the queue item in backend
            await updateQueueItem(centralDiscoveryQueue.sessionId, updatedQueueItem);
            
            // Update local queue
            await centralDiscoveryQueue.addQueueItem(updatedQueueItem);
            
            console.log('✅ EDUCATION STRATEGY: Successfully updated university suggestions based on user input');
          } else {
            console.log('🎓 EDUCATION STRATEGY: No valid school names found in user input');
          }
          
        } catch (llmError) {
          console.error('🎓 EDUCATION STRATEGY: Error processing custom school input:', llmError);
        }
        
        // Always return CONTINUE_DISCOVERY to prevent sending to backend
        return {
          ...decision,
          actionType: 'CONTINUE_DISCOVERY',
          strategyPhase: StrategyPhase.CONVERSATION
        };
      }
    }
    
    // If no custom input or no existing queue items, generate only the first education item (degrees)
    if (!hasUserMessage) {
      // Check if we already have education queue items to prevent duplicates
      const existingEducationItems = centralDiscoveryQueue.getItemsByTopic('education');
      
      if (existingEducationItems.length === 0) {
        // Generate only the first education item (degrees) to start the sequence
        console.log('🎓 EDUCATION STRATEGY: No existing education items, generating initial degree item only');
        await generateEducationData(messages, null, decision, true, 'degrees_only');
        console.log('🎓 EDUCATION STRATEGY: Successfully populated central queue with degree item');
      } else {
        console.log('🎓 EDUCATION STRATEGY: Found existing education items:', existingEducationItems.length, 'skipping generation');
      }
    }
    
    return {
      ...decision,
      actionType: 'CONTINUE_DISCOVERY',
      strategyPhase: StrategyPhase.CONVERSATION
    };
    
  } catch (error) {
    console.error('🎓 EDUCATION STRATEGY: Error:', error);
    console.error('🎓 EDUCATION STRATEGY: Error stack:', error.stack);
    
    // Instead of falling back to backend, return CONTINUE_DISCOVERY to avoid disrupting the queue flow
    // This will allow the queue system to handle the topic appropriately
    console.log('🎓 EDUCATION STRATEGY: Returning CONTINUE_DISCOVERY despite error to maintain queue flow');
    return {
      ...decision,
      actionType: 'CONTINUE_DISCOVERY',
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
};

export default educationStrategy;